import { useMarkActivationChecklistDismissedMutation } from '@/app/redux/endpoints/organisation';
import { useLessonPlans } from '@/app/redux/hooks/lessonPlans';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { useUser } from '@/app/redux/hooks/user';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import tw from '@/common/utils/twmacro';
import { CircularProgress, circularProgressClasses } from '@mui/material';
import {
  ActivityIcon,
  ArrowRightIcon,
  ChalkboardTeacherIcon,
  CheckCircleIcon,
  CheckIcon,
  GameControllerIcon,
  GearIcon,
  HandWavingIcon,
  PaperPlaneTiltIcon,
  PlusIcon,
  StudentIcon,
  UsersFourIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shadcn/components/alert-dialog';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import {
  CardDescription,
  CardHeader,
  CardTitle,
} from '@shadcn/components/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shadcn/components/popover';
import { Progress } from '@shadcn/components/progress';
import { cn } from '@shadcn/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';

const Video = ({ src, title }: { src: string; title: string }) => (
  <video
    className="w-full rounded-sm"
    src={src}
    title={title}
    autoPlay
    controls
    loop
  />
);

const checklistItems = [
  {
    icon: <StudentIcon className="size-5" weight="duotone" />,
    title: 'Set Up Learners',
    description: (
      <div className="flex flex-col space-y-4 text-sm/6">
        <p>Start by adding your learners in the Spellcaster dashboard.</p>
        <Video
          src="/videos/adding-learners-onboarding.mp4"
          title="Adding Learners to Spellcaster"
        />
        <ul className="list-disc ml-4 flex flex-col gap-1">
          <li className="ml-2">
            Select{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <PlusIcon /> Add learner
            </Badge>{' '}
            to add an individual learner, or{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <UsersFourIcon /> Bulk add learners
            </Badge>{' '}
            to add multiple learners at once.
          </li>
          <li className="ml-2">
            Fill out the learners' details. Usernames and passwords can be
            randomly generated, or you can choose your own.
          </li>
          <p className="ml-4">
            <strong>Note:</strong> You can export printable learner login cards
            by clicking the checkboxes beside learners' names after creating
            them.
          </p>
          <li className="ml-2">
            Choose whether to set their starting level manually or to let
            Spellcaster help find their place by assigning an initial
            assessment.
          </li>
          <li className="ml-2">
            Click on a learner's name and select{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <GearIcon />
              Settings
            </Badge>{' '}
            to adjust settings — including articulation gestures, audio
            segmentation, and alternative control schemes like typing. Settings
            can be adjusted individually or in bulk. Learners can also adjust
            some settings in-game.
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <GameControllerIcon className="size-5" weight="duotone" />,
    title: 'Begin Game',
    description: (
      <div className="flex flex-col space-y-4 text-sm/6">
        <p>Get your learners into their first session.</p>
        <Video
          src="/videos/game-onboarding.mp4"
          title="Getting Learners Playing"
        />
        <ul className="list-disc ml-4 flex flex-col gap-1">
          <li className="ml-2">
            Learners can sign in independently with their username and password
            from{' '}
            <a
              href={import.meta.env.VITE_MARKETING_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="link" className="p-0 m-0 h-auto">
                Spellcaster's home page
              </Button>
            </a>
            . You can also sign them in from your dashboard when working
            alongside a learner.
          </li>
          <li className="ml-2">
            Each level targets a scope and sequence level through a sequence of
            card-based mini-games — from phoneme-grapheme matching, to word
            building, to connected text — plus optional spelling duels against
            the computer. Higher levels introduce activities for morphological
            awareness.
          </li>
          <li className="ml-2">
            Speak to in-game characters to start a card game. When speaking to a
            character, click the blue and red wands to commence a duel with
            them.
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <ActivityIcon className="size-5" weight="duotone" />,
    title: 'View Results',
    description: (
      <div className="flex flex-col space-y-4 text-sm/6">
        <p>
          Once your learners have completed some activities, find out how
          they're performing by viewing their results.
        </p>
        <Video
          src="/videos/data-onboarding.mp4"
          title="Viewing Learner Results"
        />
        <ul className="list-disc ml-4 flex flex-col gap-1">
          <li className="ml-2">
            Click on a learner's name to open their profile.
          </li>
          <li className="ml-2">
            Here you can view their activity feed, assessments and other details
            about the learner.
          </li>
          <li className="ml-2">
            View the results of a minigame by clicking on it in the activity
            feed.
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <ChalkboardTeacherIcon className="size-5" weight="duotone" />,
    title: 'Create a Lesson Plan',
    description: (
      <div className="flex flex-col space-y-4 text-sm/6">
        <p>
          Create tailored structured literacy lesson plans for your learners.
        </p>
        <Video
          src="/videos/lesson-planner-onboarding.mp4"
          title="Creating a Lesson Plan"
        />
        <ul className="list-disc ml-4 flex flex-col gap-1">
          <li className="ml-2">
            Go to{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <ChalkboardTeacherIcon />
              Lesson Plans
            </Badge>{' '}
            in the sidebar.
          </li>
          <li className="ml-2">
            Click{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <PlusIcon />
              Create lesson plan
            </Badge>{' '}
            to start from scratch — drag in activity drills, populate them with
            content, and rearrange as needed — or generate a pre-filled plan
            from the three-dot menu on a learner's row or in our scope and
            sequence.
          </li>
          <li className="ml-2">
            Each lesson plan auto-generates a learner worksheet to print out and
            a slide show for presentation.
          </li>
        </ul>
      </div>
    ),
  },
  {
    icon: <PaperPlaneTiltIcon className="size-5" weight="duotone" />,
    title: 'Invite Others',
    description: (
      <div className="flex flex-col space-y-4 text-sm/6">
        <p>
          Invite others to your Spellcaster dashboard to let them manage and
          view your learners' progress.
        </p>
        <Video
          src="/videos/inviting-team-onboarding.mp4"
          title="Inviting Team Members"
        />
        <ul className="list-disc ml-4 flex flex-col gap-1">
          <li className="ml-2">
            Click your organisation name in the top-left corner of the dashboard
            and select{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <UsersIcon /> Manage & invite members
            </Badge>
            , or click the{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <PaperPlaneTiltIcon />
              Invite Team Members
            </Badge>{' '}
            button in the sidebar.
          </li>
          <li className="ml-2">
            Click{' '}
            <Badge variant="outline" className="gap-2 h-6 px-2">
              <PlusIcon /> Invite Members
            </Badge>
            .
          </li>
          <li className="ml-2">
            Enter email addresses or upload a CSV file to send invitations.
          </li>
        </ul>
      </div>
    ),
  },
];

interface ActivationChecklistContentProps {
  currentItemIdx: number;
  setCurrentItemIdx: (idx: number) => void;
  showDismiss: boolean;
  onDismissClick: () => void;
  onFinish: () => void;
}

export function ActivationChecklistContent({
  currentItemIdx,
  setCurrentItemIdx,
  showDismiss,
  onDismissClick,
  onFinish,
}: ActivationChecklistContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const currentItem = checklistItems[currentItemIdx];

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [currentItemIdx]);

  return (
    <div className="flex">
      <div className="flex flex-col gap-2 w-56 p-4">
        <CardHeader className="p-0">
          <CardTitle>Getting started</CardTitle>
          <CardDescription>
            Make the most of Spellcaster by checking off the items below to get
            yourself set up.
          </CardDescription>
        </CardHeader>

        <div className="flex flex-col gap-2 pt-6 text-sm">
          {checklistItems.map((item, index) => (
            <ChecklistItem
              key={index}
              onClick={() => setCurrentItemIdx(index)}
              className={cn(
                currentItemIdx === index &&
                  'bg-white border-neutral-300 text-black',
              )}
            >
              <div
                className={cn(
                  'flex items-center gap-2',
                  currentItemIdx === index && 'text-primary',
                )}
              >
                <div
                  className={cn(
                    'my-auto w-1 h-6 -ml-2 rounded-e-sm',
                    currentItemIdx === index ? 'bg-primary' : 'bg-transparent',
                  )}
                />
                {item.icon}
                <span>{item.title}</span>
              </div>
              {currentItemIdx > index && (
                <CheckCircleIcon
                  className="size-5 text-rag-green"
                  weight="fill"
                />
              )}
            </ChecklistItem>
          ))}
        </div>

        <div className="flex flex-col gap-1 mt-auto">
          <span className="font-semibold text-muted-foreground text-xs uppercase">
            Step {currentItemIdx + 1} of {checklistItems.length}
          </span>
          <Progress
            value={((currentItemIdx + 1) / checklistItems.length) * 100}
            className="h-1.5 bg-accent border"
          />
          {showDismiss && currentItemIdx < checklistItems.length - 1 && (
            <button
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground text-left transition-colors"
              onClick={onDismissClick}
            >
              Dismiss checklist
            </button>
          )}
        </div>
      </div>

      <div
        ref={contentRef}
        className="relative flex flex-col gap-2 rounded-md p-6 pr-8 size-[600px] overflow-y-auto shadow-sm bg-white"
      >
        <CardHeader className="p-0">
          <CardTitle>{currentItem.title}</CardTitle>
        </CardHeader>
        {currentItem.description}
        <div className="sticky bottom-0 ml-auto bg-white rounded-md">
          {currentItemIdx < checklistItems.length - 1 ? (
            <Button
              className="gap-1"
              onClick={() => {
                trackMixpanelEvent('Activation Checklist Next Clicked', {
                  nextStep: checklistItems[currentItemIdx + 1].title,
                });
                setCurrentItemIdx(currentItemIdx + 1);
              }}
            >
              Next: {checklistItems[currentItemIdx + 1].title}
              <ArrowRightIcon className="size-4" />
            </Button>
          ) : (
            <Button className="gap-2" onClick={onFinish}>
              Finish <CheckIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ActivationChecklist() {
  const { user } = useUser();
  const { organisation } = useOrganisation();
  const { students } = useStudents();
  const { lessonPlans } = useLessonPlans();
  const [dismissChecklist] = useMarkActivationChecklistDismissedMutation();

  const [open, setOpen] = useState(false);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [currentItemIdx, setCurrentItemIdx] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    // Derive completion of items based on state
    const completedItems = {
      learnersAdded: students.length > 0,
      gameStarted: students.some((s) => s.lastLogin > 0),
      hasViewedResults:
        organisation?.activationChecklist.hasViewedResults ?? false,
      hasCreatedLessonPlan: lessonPlans.length > 0,
      hasInvitedMembers: (organisation?.memberIds ?? []).length > 1,
    };

    const nextItemIdx = Object.values(completedItems).indexOf(false);

    if (nextItemIdx >= 0) {
      setCompletedCount(nextItemIdx);
      setCurrentItemIdx(nextItemIdx);
    } else {
      setCompletedCount(checklistItems.length);
      setCurrentItemIdx(checklistItems.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lessonPlans.length, organisation?.memberIds, students]);

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(open) => {
          trackMixpanelEvent(
            `Activation Checklist ${open ? 'Opened' : 'Closed'}`,
          );
          setOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <div className="flex flex-col gap-1 mx-1.5 rounded-xl border border-border bg-background p-3 hover:border-primary transition-colors cursor-pointer">
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground/80 text-green-600">
              <WavingHand className="size-5" weight="duotone" /> Hi
              {user ? `, ${user.name.split(' ')[0]}!` : '!'}
            </span>

            <span className="text-xs text-muted-foreground">
              Let's get you started with Spellcaster
            </span>

            <div className="flex items-center gap-2 pt-1">
              <CircularProgress
                variant="determinate"
                value={(completedCount / checklistItems.length) * 100}
                enableTrackSlot
                color="inherit"
                size={20}
                thickness={6}
                sx={{
                  [`& .${circularProgressClasses.circle}`]: {
                    stroke: 'hsl(var(--primary))',
                    strokeLinecap: 'round',
                  },
                  [`& .${circularProgressClasses.track}`]: {
                    stroke: 'hsl(var(--primary))',
                    opacity: 0.2,
                  },
                }}
              />
              <span className="text-sm font-medium text-primary">
                {completedCount === 0 ? (
                  <span>{checklistItems.length} tasks to complete</span>
                ) : (
                  <span>
                    {completedCount} of {checklistItems.length} tasks complete
                  </span>
                )}
              </span>
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          side="right"
          align="end"
          className="w-auto p-2 bg-neutral-50 mt-2"
        >
          <ActivationChecklistContent
            currentItemIdx={currentItemIdx}
            setCurrentItemIdx={setCurrentItemIdx}
            showDismiss={true}
            onDismissClick={() => {
              trackMixpanelEvent('Activation Checklist Dismiss Clicked');
              setDismissDialogOpen(true);
            }}
            onFinish={() => {
              trackMixpanelEvent('Activation Checklist Finish Clicked');
              setDismissDialogOpen(true);
            }}
          />
        </PopoverContent>
      </Popover>

      <AlertDialog open={dismissDialogOpen} onOpenChange={setDismissDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide setup checklist?</AlertDialogTitle>
            <AlertDialogDescription>
              You can always find setup help in our{' '}
              <a
                href="https://spellcaster.education/resources"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="link"
                  className="p-0 m-0 h-auto"
                  onClick={() =>
                    trackMixpanelEvent(
                      'Documentation Opened via Activation Checklist',
                    )
                  }
                >
                  documentation
                </Button>
              </a>{' '}
              if you need it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (organisation) {
                  dismissChecklist({ orgId: organisation!.id });
                  trackMixpanelEvent('Activation Checklist Dismiss Confirmed');
                } else {
                  toast.error('Failed to dismiss checklist', {
                    description: UNKNOWN_ERROR_MESSAGE,
                  });
                }
              }}
            >
              Yes, hide it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const ChecklistItem = tw.button`flex items-center justify-between rounded-lg hover:bg-accent cursor-pointer p-2 font-medium border border-transparent text-muted-foreground text-left text-wrap break-words`;

const WavingHand = styled(HandWavingIcon)`
  animation: wave 2.5s infinite;
  @keyframes wave {
    0% {
      transform: rotate(0deg);
    }
    10% {
      transform: rotate(14deg);
    }
    /* The following five values can be played with to make the waving more or less extreme */
    20% {
      transform: rotate(-8deg);
    }
    30% {
      transform: rotate(14deg);
    }
    40% {
      transform: rotate(-4deg);
    }
    50% {
      transform: rotate(10deg);
    }
    60% {
      transform: rotate(0deg);
    }
    /* Reset for the last half to pause */
    100% {
      transform: rotate(0deg);
    }
  }
`;
